import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { setAuthContext } from '../utils/apiClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { instance } = useMsal();
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Exchange MSAL token for internal JWT
  const exchangeToken = useCallback(async () => {
    try {
      const activeAccount = instance.getActiveAccount();
      console.log('[Auth] Active account:', activeAccount?.username);

      if (!activeAccount) {
        throw new Error('No active account');
      }

      // Get MSAL access token silently
      console.log('[Auth] Acquiring token silently...');
      let msalResponse;
      try {
        msalResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account: activeAccount,
        });
        console.log('[Auth] MSAL token acquired, scopes:', msalResponse.scopes);
      } catch (silentError) {
        console.error('[Auth] Silent token acquisition failed:', silentError);
        // Try interactive if silent fails
        msalResponse = await instance.acquireTokenPopup({
          ...loginRequest,
          account: activeAccount,
        });
        console.log('[Auth] MSAL token acquired via popup');
      }

      // Exchange MSAL ID token for internal JWT
      // Note: We use idToken instead of accessToken because accessToken is for MS Graph API
      console.log('[Auth] Exchanging ID token with backend...');
      const response = await fetch('/api/auth/exchange', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          msalAccessToken: msalResponse.idToken,
        }),
      });

      const data = await response.json();
      console.log('[Auth] Exchange response:', { status: response.status, ok: data.ok, error: data.error });

      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Token exchange failed');
      }

      setAccessToken(data.accessToken);
      setUser(data.user);
      setIsAuthenticated(true);

      console.log('[Auth] Token exchange successful, user:', data.user?.email);
      return data.accessToken;
    } catch (error) {
      console.error('[Auth] Token exchange error:', error);
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  }, [instance]);

  // Get valid access token
  const getAccessToken = useCallback(async () => {
    // If token exists, return it (no expiry)
    if (accessToken) {
      return accessToken;
    }

    // No token, try full exchange if MSAL account exists
    const activeAccount = instance.getActiveAccount();
    if (activeAccount) {
      try {
        return await exchangeToken();
      } catch {
        return null;
      }
    }

    return null;
  }, [accessToken, exchangeToken, instance]);

  // Logout
  const logout = useCallback(async () => {
    try {
      // Call backend logout
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });

      // Clear local state
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);

      // MSAL logout
      const activeAccount = instance.getActiveAccount();
      if (activeAccount) {
        await instance.logoutPopup({
          postLogoutRedirectUri: '/',
          mainWindowRedirectUri: '/',
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Force clear state even on error
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  }, [instance]);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        // Check for MSAL account and exchange token
        const activeAccount = instance.getActiveAccount();
        if (activeAccount) {
          await exchangeToken();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [instance, exchangeToken]);

  const value = {
    accessToken,
    user,
    isLoading,
    isAuthenticated,
    exchangeToken,
    getAccessToken,
    logout,
  };

  // Wire up apiClient with auth context
  useEffect(() => {
    setAuthContext(value);
  }, [value]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
